'use client';

import { useProductViewRecord } from '@/hooks/use-product-view-record';
import { ADD_WEDDING_GUIDE } from '@/typed-graphql/mutation';
import { validateEmail2 } from '@/util/formValidators';
import { gql, useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import EventDatePicker from '../event-date-picker';
import FormError from '../FormError';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const WeddingGuidForm = () => {
  const [showGuideList, setShowGuideList] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  const { formState, register, handleSubmit, control, getValues, reset } = useForm({
    defaultValues: {
      email: '',
      wedding_date: null,
      guide: [] as number[],
    },
  });

  const [reqAddWeddingGuid, { loading, error, data }] = useMutation(gql(ADD_WEDDING_GUIDE));
  const { setLocalStorageEmail } = useProductViewRecord();

  const handleSubmitWeddingGuide = values => {
    if (loading) return;
    const { wedding_date } = values;

    reqAddWeddingGuid({
      variables: {
        input: {
          ...values,
          wedding_date: wedding_date ? dayjs(wedding_date).format('YYYY-MM-DD') : undefined,
        },
      },
    })
      .then(({ data }) => {
        if (String(data?.addWeddingGuide?.code) === '200') {
          setLocalStorageEmail(values.email);
        }
      })
      .finally(() => {
        reset();
      });
  };

  return (
    <form
      onSubmit={handleSubmit(handleSubmitWeddingGuide)}
      className='mx-auto mt-2.5 max-w-[1100px] px-[22px] lg_mt-5 lg_border-1 lg_border-solid lg_border-E7DED5 lg_p-[50px]'
    >
      <div className='flex flex-col gap-y-5 lg_gap-y-0'>
        <div className='flex flex-col gap-y-5 lg_flex-row lg_gap-x-6'>
          <div className='flex-1'>
            <div className='text-[15px] leading-[1.4]'>Email:</div>
            <input
              {...register('email', {
                validate: value => validateEmail2(typeof value === 'string' ? value.trim() : ''),
              })}
              className='mt-[5px] h-10 w-full border-1  border-solid border-E7DED5 px-[13px] text-[13px] outline-none lg_text-[14px]'
            />
            <ErrorMessage
              name='email'
              errors={formState.errors}
              render={({ message }) => {
                return (
                  <div className='relative'>
                    <span className='absolute text-xs text-e73635'>{message}</span>
                  </div>
                );
              }}
            />
          </div>
          <div className='flex-1'>
            <div className='text-[15px] leading-[1.4]'>Guide:</div>
            <div
              className='mt-[5px] flex h-10 w-full  cursor-pointer items-center justify-between border-1 border-solid border-E7DED5 px-[13px] '
              onClick={() => {
                const selectedGuides = getValues('guide');

                if (Array.isArray(selectedGuides) && selectedGuides.length !== 0) {
                  return;
                }

                setShowGuideList(x => !x);
              }}
            >
              <span className='text-[13px] leading-[1.4] text-aba6a0 lg_text-[15px]'>I need</span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='10'
                height='5'
                fill='none'
                viewBox='0 0 10 5'
              >
                <path
                  fill='#80583F'
                  d='M0 5h2.07L5 2.07 7.93 5H10L5 0z'
                ></path>
              </svg>
            </div>

            <Controller
              name='guide'
              control={control}
              rules={{
                validate: value => {
                  if (!Array.isArray(value) || value.length === 0) return 'This field is required.';
                },
              }}
              render={({ field, fieldState, formState }) => {
                return (
                  <div
                    className={clsx(
                      'mt-2.5  flex-col items-start gap-y-[15px] py-[15px] pl-[15px] shadow-[0_0_5px_0_rgba(0,0,0,0.25)] lg_gap-y-2.5',
                      showGuideList ? 'flex' : 'hidden',
                    )}
                  >
                    {[
                      'Wedding Planning List',
                      'Swatches and Fabric Guide',
                      'Wedding and Bridesmaid Dress Color Suggestions',
                      'Fit and Style Testing',
                      'All of them',
                    ].map((x, i) => {
                      return (
                        <button
                          key={x}
                          className='flex items-center gap-x-[5px]'
                          type='button'
                          onClick={() => {
                            field.onChange(
                              field.value.includes(i + 1)
                                ? field.value.filter(x => x !== i + 1)
                                : field.value.concat(i + 1),
                            );
                          }}
                        >
                          <div
                            className={clsx(
                              'relative h-4 w-4 flex-none rounded-[2px] border-1 border-solid border-aba6a0',
                              field.value.includes(i + 1) &&
                                clsx(
                                  'after_h-2.5 after_w-2.5 after_rounded-[2px] after_content-[""]',
                                  'after_absolute after_left-1/2 after_top-1/2 after_-translate-x-1/2 after_-translate-y-1/2 after_bg-80583F',
                                ),
                            )}
                          ></div>
                          <div className='text-[13px] leading-[1.4] lg_text-[15px]'>{x}</div>
                        </button>
                      );
                    })}
                  </div>
                );
              }}
            />

            <ErrorMessage
              name='guide'
              errors={formState.errors}
              render={({ message }) => {
                return (
                  <div className='relative'>
                    <span className='absolute text-xs text-e73635'>{message}</span>
                  </div>
                );
              }}
            />
          </div>
        </div>
        <div className='w-full text-[0] lg_mt-5  lg_w-[calc((100%-24px)/2)]'>
          <div className='text-[15px] leading-[1.4]'>My Wedding Date:</div>
          <Controller
            control={control}
            name='wedding_date'
            render={({ field: { onChange, value } }) => {
              return (
                <Popover
                  open={datePickerVisible}
                  onOpenChange={nextOpen => {
                    setDatePickerVisible(nextOpen);
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type='button'
                      className='mt-[5px] w-full'
                    >
                      <div className='relative  flex h-10 items-center gap-x-2.5 border-1 border-solid border-E7DED5 bg-white px-[13px] lg_h-10'>
                        <div
                          className={clsx(
                            ' flex h-full items-center ',
                            'rounded-none border-none outline-0',
                            'bg-transparent',
                            'flex-1',
                            'text-[13px] leading-[1.4] lg_text-[14px]',
                            !value && 'text-aba6a0',
                          )}
                        >
                          {value ? dayjs(value).format('MM/DD/YYYY') : null}
                        </div>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className='shadow-md '>
                    <EventDatePicker
                      onDone={date => {
                        setDatePickerVisible(false);
                        onChange(date);
                      }}
                      disabledDate={dayjs().millisecond(0).second(0).minute(0).hour(0).toDate()}
                      value={value}
                    />
                  </PopoverContent>
                </Popover>
              );
            }}
          />
        </div>

        <div className='mx-auto w-full max-w-[250px] lg_mt-10 lg_max-w-[488px]'>
          <button
            type='submit'
            className={clsx(
              ' min-h-[48px]  rounded-[3px] bg-80583F text-white lg_min-h-[60px]  lg_enabled_hover_bg-white lg_enabled_hover_text-80583F',
              'border-1 border-solid border-80583F',
              'flex w-full items-center justify-center text-center',
            )}
            disabled={loading}
          >
            GET NOW{loading && '...'}
          </button>
          <FormError errors={[error]} />
          {data?.addWeddingGuide?.content ? <div className='mt-2.5'>{data?.addWeddingGuide?.content}</div> : null}
        </div>
      </div>
    </form>
  );
};

export default WeddingGuidForm;
